import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Alert,
  Statistic,
  Space,
  Typography,
  Checkbox,
  message,
  Tag,
  Empty,
  Descriptions,
} from "antd";
import {
  DollarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { getApi, postApi, moneyFormat, isBetaUser } from "@utils";
import moment from "moment";

const { Title, Text } = Typography;

const ConsignorTransferUI = () => {
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [selectedPayouts, setSelectedPayouts] = useState([]); // Empty by default
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [transferResult, setTransferResult] = useState(null);

  // Check if user is beta user
  const isBeta = isBetaUser();

  useEffect(() => {
    fetchPendingPayouts();
  }, []);

  const fetchPendingPayouts = async () => {
    try {
      setLoading(true);
      const response = await getApi("getStripePendingTransfers", {});

      if (response?.data) {
        setPendingPayouts(response.data.payouts || []);
        setSummary(response.data.summary);
        // Don't pre-select anything
        setSelectedPayouts([]);
      }
    } catch (error) {
      message.error("Failed to load pending payouts");
    } finally {
      setLoading(false);
    }
  };

  const togglePayout = (payoutId) => {
    setSelectedPayouts((prev) =>
      prev.includes(payoutId)
        ? prev.filter((id) => id !== payoutId)
        : [...prev, payoutId]
    );
  };

  const toggleAll = () => {
    if (selectedPayouts.length === pendingPayouts.length) {
      setSelectedPayouts([]);
    } else {
      setSelectedPayouts(pendingPayouts.map((p) => p.id));
    }
  };

  const getSelectedTotal = () => {
    const selected = pendingPayouts.filter((p) =>
      selectedPayouts.includes(p.id)
    );
    return selected.reduce(
      (sum, p) => sum + parseFloat(p.cashout_amount || 0),
      0
    );
  };

  const getSelectedTotalCents = () => {
    return Math.round(getSelectedTotal() * 100);
  };

  const handleInitiateTransfer = () => {
    if (selectedPayouts.length === 0) {
      message.error("Please select at least one payout to transfer");
      return;
    }
    setShowConfirmation(true);
  };

  const confirmTransfer = async () => {
    try {
      setTransferring(true);

      const response = await postApi("postStripeTransfersInitiate", {
        payout_ids: selectedPayouts,
        confirm_amount_cents: getSelectedTotalCents(),
      });

      if (response?.data) {
        message.success(
          `Successfully initiated ${response.data.summary.successful} transfer(s)`
        );
        setTransferResult({
          success: true,
          details: response.data,
        });
        // Refresh the list
        await fetchPendingPayouts();
        setShowConfirmation(false);
        setSelectedPayouts([]);
      } else {
        message.error(response.message || "Transfer failed");
        setTransferResult({
          success: false,
          message: response.message,
        });
      }
    } catch (error) {
      message.error(error.message || "Transfer failed");
      setTransferResult({
        success: false,
        message: error.message || "Network error occurred",
      });
    } finally {
      setTransferring(false);
    }
  };

  if (!isBeta) {
    return (
      <Alert
        message="Feature Not Available"
        description="Stripe transfers are not yet enabled for your account."
        type="info"
        showIcon
      />
    );
  }

  if (!summary?.stripe_account_connected) {
    return (
      <Alert
        message="Stripe Account Not Connected"
        description="You need to connect your Stripe account before you can receive transfers."
        type="warning"
        showIcon
        action={
          <Button type="primary" size="small" icon={<WalletOutlined />}>
            Connect Stripe Account
          </Button>
        }
      />
    );
  }

  const columns = [
    {
      title: (
        <Checkbox
          checked={
            selectedPayouts.length === pendingPayouts.length &&
            pendingPayouts.length > 0
          }
          indeterminate={
            selectedPayouts.length > 0 &&
            selectedPayouts.length < pendingPayouts.length
          }
          onChange={toggleAll}
        >
          Select All
        </Checkbox>
      ),
      dataIndex: "id",
      key: "select",
      width: 80,
      fixed: "left",
      render: (id) => (
        <Checkbox
          checked={selectedPayouts.includes(id)}
          onChange={() => togglePayout(id)}
        />
      ),
    },
    {
      title: "Product",
      dataIndex: "product_image",
      key: "image",
      width: 80,
      render: (image, record) => (
        <img
          src={image || "/placeholder-image.png"}
          alt={record.product_title}
          style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4 }}
        />
      ),
    },
    {
      title: "Title",
      dataIndex: "product_title",
      key: "product",
      render: (title) => title || "N/A",
    },
    {
      title: "Order #",
      dataIndex: "shopify_id",
      key: "order_id",
      render: (id) => (id ? `#${id}` : "N/A"),
    },
    {
      title: "Sale Amount",
      dataIndex: "price_paid",
      key: "price_paid",
      align: "right",
      render: (amount) => moneyFormat(parseFloat(amount || 0)),
    },
    {
      title: "Your Payout",
      dataIndex: "cashout_amount",
      key: "cashout_amount",
      align: "right",
      render: (amount) => (
        <Text strong style={{ color: "#52c41a" }}>
          {moneyFormat(parseFloat(amount || 0))}
        </Text>
      ),
    },
    {
      title: "Date",
      dataIndex: "sold_at",
      key: "sold_at",
      render: (date) =>
        date ? moment.unix(date).format("MMM DD, YYYY") : "N/A",
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card>
        <Title level={4}>
          <DollarOutlined /> Transfer Payouts
        </Title>
        <Text type="secondary">
          Select payouts to transfer to your bank account
        </Text>
      </Card>

      {/* Summary Statistics */}
      {summary && (
        <Card>
          <Space
            size="large"
            style={{ width: "100%", justifyContent: "space-around" }}
          >
            <Statistic
              title="Total Pending"
              value={summary.total_cashout_dollars || 0}
              precision={2}
              prefix="$"
            />
            <Statistic
              title="Available Payouts"
              value={summary.count || 0}
              suffix={summary.count === 50 ? "(max)" : ""}
            />
            <Statistic
              title="Selected Amount"
              value={getSelectedTotal()}
              precision={2}
              prefix="$"
              valueStyle={{ color: "#52c41a" }}
            />
          </Space>
        </Card>
      )}

      {/* Transfer Result Alert */}
      {transferResult && (
        <Alert
          message={
            transferResult.success ? "Transfer Successful" : "Transfer Failed"
          }
          description={
            transferResult.success
              ? `Successfully initiated transfer${
                  transferResult.details?.results?.length > 1 ? "s" : ""
                }`
              : transferResult.message
          }
          type={transferResult.success ? "success" : "error"}
          showIcon
          closable
          onClose={() => setTransferResult(null)}
        />
      )}

      {/* Payouts Table */}
      <Card>
        {pendingPayouts.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No pending payouts available"
          >
            <Text type="secondary">
              Payouts will appear here once they're confirmed and ready to
              transfer
            </Text>
          </Empty>
        ) : (
          <>
            <Table
              dataSource={pendingPayouts}
              columns={columns}
              rowKey="id"
              loading={loading}
              pagination={false}
              scroll={{ x: 1000 }}
              rowClassName={(record) =>
                selectedPayouts.includes(record.id)
                  ? "ant-table-row-selected"
                  : ""
              }
            />

            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text type="secondary">
                {selectedPayouts.length} payout(s) selected
              </Text>
              <Button
                type="primary"
                size="large"
                icon={<DollarOutlined />}
                onClick={handleInitiateTransfer}
                disabled={selectedPayouts.length === 0 || transferring}
              >
                Transfer {moneyFormat(getSelectedTotal())}
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* Confirmation Modal */}
      <Modal
        title="Confirm Transfer"
        visible={showConfirmation}
        onOk={confirmTransfer}
        onCancel={() => setShowConfirmation(false)}
        confirmLoading={transferring}
        okText="Confirm Transfer"
        okButtonProps={{ size: "large" }}
        cancelButtonProps={{ size: "large" }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Alert
            message="Review Transfer Details"
            description="Please confirm the transfer amount before proceeding."
            type="info"
            showIcon
          />

          <Card
            style={{ backgroundColor: "#f0f5ff", border: "1px solid #adc6ff" }}
          >
            <div style={{ textAlign: "center" }}>
              <Text type="secondary">Transfer Amount</Text>
              <Title level={2} style={{ margin: "8px 0", color: "#1890ff" }}>
                {moneyFormat(getSelectedTotal())}
              </Title>
            </div>
          </Card>

          <Alert
            message="Processing Time"
            description="This transfer typically takes 1-3 business days to reach your bank account."
            type="warning"
            showIcon
          />

          <Descriptions column={1} size="small">
            <Descriptions.Item label="Selected Payouts">
              <Text strong>{selectedPayouts.length}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Space>
      </Modal>
    </Space>
  );
};

export { ConsignorTransferUI };
